import { ReactNode, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { QrCode, LogOut, LayoutDashboard, Link2, BarChart3, Menu, X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const navItem = ({ isActive }: { isActive: boolean }) =>
  `flex items-center gap-1.5 text-sm transition-colors duration-150 ${
    isActive ? 'text-foreground' : 'text-text-muted hover:text-foreground'
  }`;

const mobileNavItem = ({ isActive }: { isActive: boolean }) =>
  `flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-colors duration-150 ${
    isActive ? 'text-foreground bg-background' : 'text-text-muted hover:text-foreground hover:bg-background'
  }`;

export function Layout({ children }: { children: ReactNode }) {
  const { user, signOut } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const closeMobileMenu = () => setMobileMenuOpen(false);

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-surface">
        <div className="max-w-5xl mx-auto px-4 h-14 flex items-center justify-between gap-6">
          <div className="flex items-center gap-6">
            {/* Hamburger button - mobile only */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="sm:hidden flex items-center justify-center text-text-muted hover:text-foreground transition-colors duration-150 cursor-pointer"
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
            <div className="flex items-baseline gap-1.5 leading-none">
              <span className="font-semibold text-foreground">andmore</span>
              <span className="font-medium text-accent">profile</span>
            </div>
            <nav className="hidden sm:flex items-center gap-4">
              <NavLink to="/dashboard" end className={navItem}>
                <LayoutDashboard className="w-4 h-4" />
                <span>Dashboard</span>
              </NavLink>
              <NavLink to="/qrs" className={navItem}>
                <QrCode className="w-4 h-4" />
                <span>QR Codes</span>
              </NavLink>
              <NavLink to="/pages" className={navItem}>
                <Link2 className="w-4 h-4" />
                <span>Pages</span>
              </NavLink>
              <NavLink to="/analytics" className={navItem}>
                <BarChart3 className="w-4 h-4" />
                <span>Analytics</span>
              </NavLink>
            </nav>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-text-muted hidden sm:block">{user?.email}</span>
            <button
              onClick={signOut}
              className="flex items-center gap-1.5 text-sm text-text-muted hover:text-foreground transition-colors duration-150 cursor-pointer"
            >
              <LogOut className="w-4 h-4" />
              <span className="hidden sm:inline">Sign out</span>
            </button>
          </div>
        </div>

        {/* Mobile menu dropdown */}
        {mobileMenuOpen && (
          <nav className="sm:hidden border-t border-border bg-surface px-4 py-2 flex flex-col gap-1">
            <NavLink to="/dashboard" end className={mobileNavItem} onClick={closeMobileMenu}>
              <LayoutDashboard className="w-4 h-4" />
              <span>Dashboard</span>
            </NavLink>
            <NavLink to="/qrs" className={mobileNavItem} onClick={closeMobileMenu}>
              <QrCode className="w-4 h-4" />
              <span>QR Codes</span>
            </NavLink>
            <NavLink to="/pages" className={mobileNavItem} onClick={closeMobileMenu}>
              <Link2 className="w-4 h-4" />
              <span>Pages</span>
            </NavLink>
            <NavLink to="/analytics" className={mobileNavItem} onClick={closeMobileMenu}>
              <BarChart3 className="w-4 h-4" />
              <span>Analytics</span>
            </NavLink>
            {user?.email && (
              <div className="px-3 py-2 text-xs text-text-muted border-t border-border mt-1 pt-2">
                {user.email}
              </div>
            )}
          </nav>
        )}
      </header>
      <main className="max-w-5xl mx-auto px-4 py-8">
        {children}
      </main>
    </div>
  );
}
